/**
 * Réconciliation des gemmes payées — **logique pure** (ROADMAP 0.1, GEMMES lot 0).
 *
 * ══ Le défaut que ce module referme ══════════════════════════════════════════
 *
 * Le solde de gemmes vivait entièrement dans le snapshot, donc dans `localStorage` :
 * vider le cache de son navigateur faisait perdre des gemmes achetées avec une carte
 * bancaire. Le registre serveur `gem_grants` existe maintenant ; ce module est la
 * moitié cliente, celle qui décide QUOI créditer.
 *
 * ══ Le raisonnement, en une ligne ════════════════════════════════════════════
 *
 *     à créditer = total octroyé par le SERVEUR − ce que ce snapshot a déjà encaissé
 *
 * `paidGemsClaimed` est ce second terme : un **repère de crue**, pas un compteur de
 * gemmes. Il ne dit pas « j'ai tant de gemmes payées », il dit « j'ai déjà absorbé le
 * registre jusqu'à ce niveau ». C'est ce qui rend l'opération idempotente : la rejouer
 * dix fois de suite ne crédite qu'une fois, et une sauvegarde effacée repart de zéro
 * donc récupère tout.
 *
 * ══ Pourquoi le repère ne REDESCEND jamais ═══════════════════════════════════
 *
 * `Math.max` des deux côtés, et ce n'est pas de la prudence décorative. Un total
 * serveur plus BAS que le repère est un cas réel : le joueur s'est connecté à un
 * autre compte, ou la session anonyme a changé. Aligner le repère vers le bas
 * rouvrirait la porte au double crédit dès que le total remonte — on aurait construit
 * un exploit là où on corrigeait une perte.
 *
 * Le prix de ce choix est connu et accepté : dans ce cas-là, le joueur ne se voit pas
 * recréditer tant que l'autre compte n'a pas dépassé le repère. C'est le sens de la
 * dissymétrie — on préfère une gemme non recréditée à une gemme créditée deux fois,
 * parce que la première se corrige à la main et la seconde casse l'économie.
 *
 * ══ Ce que ce module N'EST PAS ═══════════════════════════════════════════════
 *
 * Il ne rend pas le solde autoritatif côté serveur. Le §1 veut un jeu jouable en moins
 * de cinq secondes sans compte, et le §6 des gains hors-ligne : un solde qui exigerait
 * un aller-retour réseau à chaque jalon casserait les deux. Seul ce qui a été PAYÉ —
 * ce dont la perte est un litige — passe par le serveur.
 */

import type { CitySnapshot } from './gameState';

export interface PaidGemsReconciliation {
  readonly snapshot: CitySnapshot;
  /** Gemmes effectivement ajoutées au solde. 0 quand il n'y avait rien à rattraper. */
  readonly credited: number;
}

/**
 * Aligne le snapshot sur le total octroyé par le serveur.
 *
 * Rend le snapshot inchangé (l'objet lui-même, pas une copie) quand il n'y a rien à
 * faire : l'appelant peut donc comparer les références pour éviter une écriture de
 * sauvegarde et un rendu inutiles à chaque démarrage.
 *
 * @param serverTotal Somme de `gem_grants` pour ce joueur. Une valeur non finie ou
 *   négative — réponse tronquée, champ absent, serveur en panne — est traitée comme
 *   « rien à dire », jamais comme un zéro : un zéro ferait office de vérité et
 *   retiendrait à jamais un crédit légitime.
 */
export function reconcilePaidGems(
  snapshot: CitySnapshot,
  serverTotal: number,
): PaidGemsReconciliation {
  if (!Number.isFinite(serverTotal) || serverTotal < 0) {
    return { snapshot, credited: 0 };
  }

  const total = Math.floor(serverTotal);
  const claimed = Math.max(0, Math.floor(snapshot.paidGemsClaimed));
  const credited = Math.max(0, total - claimed);

  if (credited === 0) {
    // Le repère peut malgré tout avoir besoin d'être NETTOYÉ : une sauvegarde éditée
    // à la main peut porter un repère fractionnaire ou négatif. On ne réécrit que
    // dans ce cas — sinon l'objet sort tel quel, et l'appelant le voit à la référence.
    return claimed === snapshot.paidGemsClaimed
      ? { snapshot, credited: 0 }
      : { snapshot: { ...snapshot, paidGemsClaimed: claimed }, credited: 0 };
  }

  return {
    snapshot: {
      ...snapshot,
      gems: snapshot.gems + credited,
      paidGemsClaimed: Math.max(claimed, total),
    },
    credited,
  };
}
